<template>
  <tr>
    <!-- Scores and Teams Column (Far Left) -->
    <td class="relative px-3 py-3 whitespace-nowrap">
      <router-link :to="seriesLink" tabindex="-1" aria-hidden="true" class="absolute inset-0" />
      <div class="flex items-stretch gap-1">
        <!-- Event Logo Box -->
        <div class="w-[52px] h-[52px] bg-gray-800 rounded flex items-center justify-center flex-shrink-0">
          <img
            v-if="props.series.event_logo_url"
            :src="props.series.event_logo_url"
            :alt="props.series.event"
            class="w-6 h-6 object-contain"
            style="max-width: 100%; height: auto;"
            @error="handleImageError"
            @load="handleImageLoad"
          />
          <div v-else class="w-6 h-6 bg-gray-700 rounded flex items-center justify-center">
            <span class="text-gray-400 text-xs font-bold">{{ props.series.event?.charAt(0) }}</span>
          </div>
        </div>

        <!-- Scores and Teams -->
        <div class="flex flex-col gap-1">
          <!-- Team 1 Row -->
          <div class="flex items-center gap-2">
            <div
              class="px-2 py-1 text-xs font-medium rounded"
              :class="props.series.team1.score > props.series.team2.score ? 'bg-success-900 text-success-300' : 'bg-danger-900 text-danger-300'"
            >
              {{ props.series.team1.score }}
            </div>
            <img
              v-if="props.series.team1.logo.night_url"
              :src="props.series.team1.logo.night_url"
              :alt="props.series.team1.name"
              class="w-4 h-4 rounded object-contain"
              style="max-width: 100%; height: auto;"
              @error="handleImageError"
              @load="handleImageLoad"
            />
            <div
              v-else
              class="w-4 h-4 rounded bg-gray-700 flex items-center justify-center"
            >
              <span class="text-gray-400 text-xs">?</span>
            </div>
            <span class="text-text-primary text-xs">{{ props.series.team1.name }}</span>
          </div>

          <!-- Team 2 Row -->
          <div class="flex items-center gap-2">
            <div
              class="px-2 py-1 text-xs font-medium rounded"
              :class="props.series.team2.score > props.series.team1.score ? 'bg-success-900 text-success-300' : 'bg-danger-900 text-danger-300'"
            >
              {{ props.series.team2.score }}
            </div>
            <img
              v-if="props.series.team2.logo.night_url"
              :src="props.series.team2.logo.night_url"
              :alt="props.series.team2.name"
              class="w-4 h-4 rounded object-contain"
              style="max-width: 100%; height: auto;"
              @error="handleImageError"
              @load="handleImageLoad"
            />
            <div
              v-else
              class="w-4 h-4 rounded bg-gray-700 flex items-center justify-center"
            >
              <span class="text-gray-400 text-xs">?</span>
            </div>
            <span class="text-text-primary text-xs">{{ props.series.team2.name }}</span>
          </div>
        </div>
      </div>
    </td>

    <!-- Series Details Column -->
    <td class="relative px-6 py-4 whitespace-nowrap">
      <router-link :to="seriesLink" tabindex="-1" aria-hidden="true" class="absolute inset-0" />
      <div class="flex flex-col gap-1">
        <div class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.pro_series.event') }}</div>
        <div class="text-text-primary font-medium text-sm">{{ props.series.event }}</div>
      </div>
    </td>

    <!-- Tournament Column -->
    <td class="relative px-6 py-4 whitespace-nowrap">
      <router-link :to="seriesLink" tabindex="-1" aria-hidden="true" class="absolute inset-0" />
      <div class="flex flex-col gap-1">
        <div class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('common.date') }}</div>
        <div class="time text-text-primary text-sm" data-time-format="MMM Do, h:mm A" :data-unix="props.series.played_on">{{ formatDate(props.series.played_on) }}</div>
      </div>
    </td>

    <!-- Maps Column -->
    <td class="relative px-6 py-4 whitespace-nowrap">
      <router-link :to="seriesLink" tabindex="-1" aria-hidden="true" class="absolute inset-0" />
      <div class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.pro_series.format') }}</div>
      <div class="text-text-primary text-sm">{{ getBestOfFormat() }}</div>
    </td>

    <!-- Action Column -->
    <td class="relative px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
      <router-link
        :to="seriesLink"
        :aria-label="`${props.series.team1.name} vs ${props.series.team2.name}`"
        class="group static focus:outline-none after:absolute after:inset-0"
      >
        <span class="inline-flex items-center px-2 py-2 border duration-300 border-transparent shadow-sm text-xs rounded-md text-white bg-gray-800 group-hover:bg-gray-700 group-focus:ring-2 group-focus:ring-offset-2 group-focus:ring-indigo-500">
          <ArrowRightIcon class="w-4 h-4 text-primary-400" />
        </span>
      </router-link>
    </td>
  </tr>
</template>

<script setup>
import { computed } from 'vue';
import moment from 'moment';
import { ArrowRightIcon } from '@heroicons/vue/24/outline';
import { getBestOfFormat as getBestOfFormatUtil } from '../../../../utils/array_utils';

const props = defineProps({
  series: {
    type: Object,
    required: true,
  },
});

const seriesLink = computed(() => ({ name: 'ProSeriesPage', params: { id: props.series.uuid } }));

function formatDate(dateString) {
  return moment(dateString).format('MMM Do, h:mm A');
}

function getBestOfFormat() {
  return getBestOfFormatUtil(props.series);
}

function handleImageError(event) {
  // Hide the broken image and show fallback
  event.target.style.display = 'none';
}

function handleImageLoad(event) {
  // Ensure the image is visible when it loads successfully
  event.target.style.display = 'block';
}
</script>
